import React, {useMemo} from 'react';

import {useQuery} from '@apollo/client';
import {merge} from 'lodash-es';
import {AccountContact} from '../../schema-types/AccountContact';
import {LIST_CONTACTS_SLOW} from '../AccountContactsGrid/accountContactQueries';
import {
  ListContactsSlow,
  ListContactsSlowVariables,
} from '../../schema-types/ListContactsSlow';
import useAccountId from '../../hooks/useAccountId';
import {BusinessUnitEnum} from '../../schema-types/globalTypes';
import {useAccountContext} from '../../contexts/AccountContext';
import {AccountDetailsFragment} from '../../schema-types/AccountDetailsFragment';
import validateUUID from '../../utils/validateUUID';
import {AccountContactSlow} from '../../schema-types/AccountContactSlow';

export const defaultAccountContact: AccountContact = {
  __typename: 'ContactType',
  id: null,
  curoId: null,
  fullName: null,
  accountContacts: [],
  primaryPhone: null,
  contactStatus: null,
  homePhone: null,
  dob: null,
  isEmailUnique: true,
  isDeleted: false,
  email: null,
  name: null,
  businessUnit: {
    divisionName: BusinessUnitEnum.SFP,
    __typename: 'BusinessUnitType',
    partnershipType: BusinessUnitEnum.SFP,
    id: '',
  },
  firstName: null,
  spouseName: null,
  simplifiedName: null,
  isVulnerable: null,
  isMinor: null,
  statuses: null,
  addresses: [],
  fullAddress: null,
  atrStatus: null,
  ecqStatus: null,
  nationalInsurance: null,
  nationality: null,
  title: null,
  lastName: null,
  workPhone: null,
  secondaryPhone: null,
  dataValidation: null,
};

export const updateFastAccountContacts = ({
  newData,
  contacts,
  setContacts,
}: {
  newData: AccountDetailsFragment | undefined | ListContactsSlow;
  contacts: AccountContact[];
  setContacts: React.Dispatch<React.SetStateAction<AccountContact[]>>;
}) => {
  const newContacts: AccountContact[] =
    newData?.contacts?.map((contact) => {
      const oldOrDefaultContact =
        contacts.find((x) => x.id === contact.id) || defaultAccountContact;
      if (!contact) {
        return oldOrDefaultContact;
      }
      const newContact = merge({}, oldOrDefaultContact, contact);
      return newContact;
    }) || [];
  setContacts(newContacts);
};

export const useFastAccountContacts = () => {
  const {accountId} = useAccountId();
  const {account} = useAccountContext();

  const {data: slowData} = useQuery<ListContactsSlow, ListContactsSlowVariables>(
    LIST_CONTACTS_SLOW,
    {
      variables: {
        accountId: validateUUID(accountId)!,
      },
      skip: !accountId,
    }
  );
  const contacts: (AccountContact & AccountContactSlow)[] = useMemo(() => {
    const slowDataContacts = slowData?.contacts || [];
    const returnValue =
      account?.contacts?.map((x) =>
        merge(
          {},
          x,
          slowDataContacts.find((y) => x.id === y.id)
        )
      ) || [];
    return returnValue;
  }, [account?.contacts, slowData]);

  return {contacts};
};
